import {
  Reducer,
  configureStore,
  combineSlices,
  Slice,
} from '@reduxjs/toolkit';
import { mergeDeepRight } from 'ramda';
import {
  combineEpics,
  createEpicMiddleware,
  type Epic,
} from 'redux-observable';
import { BehaviorSubject, mergeMap, Observable } from 'rxjs';

import externalGameTypesReducer from '@ezugi/bootstrap/src/store/reducers/externalGameTypes';
import { createControlPanelTools, createSnapshotEnhancer } from '@ezugi/control-panel';
import { GenericPayloadType } from '@ezugi/types';

import * as actions from './actions';
import * as constants from './constants';
import analyticsEpic from './epics/analytics';
import bonusEpic from './epics/bonus';
import configEpic from './epics/config';
import errorEpic from './epics/error';
import gameEpic from './epics/game';
import incidentsEpic from './epics/incidents';
import lobbyEpic from './epics/lobby';
import localeEpic from './epics/locale';
import mediaEpic from './epics/media';
import menuEpic from './epics/menu';
import pingEpic from './epics/ping';
import playerHistoryEpic from './epics/playerHistory';
import postMessageEpic from './epics/postMessage';
import realityCheckEpic from './epics/realityCheck';
import realtimeBalanceEpic from './epics/realtimeBalance';
import regulationsEpic from './epics/regulations';
import regulationsEpicOSS from './epics/regulationsOSS';
import roundEpic from './epics/round';
import settingsEpic from './epics/settings';
import socketEpic from './epics/socket';
import soundEpic from './epics/sound';
import tipsEpic from './epics/tips';
import videoEpic from './epics/video';
import wakeLockEpic from './epics/wakelock';
import appReducer from './reducers/app';
import authReducer from './reducers/auth';
import bonusReducer from './reducers/bonus';
import configReducer from './reducers/config';
import dialogReducer from './reducers/dialog';
import errorReducer from './reducers/error';
import flyingChipsReducer from './reducers/flyingChips';
import gameReducer from './reducers/game';
import lobbyReducer from './reducers/lobby';
import localeReducer from './reducers/locale';
import mediaSliceBuilder from './reducers/media';
import menuReducer from './reducers/menu';
import notificationReducer from './reducers/notification';
import performanceReducer from './reducers/performance';
import playerHistoryReducer from './reducers/playerHistory';
import preloaderReducer from './reducers/preloader';
import roundReducer from './reducers/round';
import socketReducer from './reducers/socket';
import tableReducer from './reducers/table';
import tipsReducer from './reducers/tips';
import uiReducer from './reducers/ui';
import userReducer from './reducers/user';
import videoReducer from './reducers/video';
import * as selectors from './selectors';
import { RootState } from './types';

const mediaSlice = mediaSliceBuilder(actions);

const bootstrapReducers = {
  config: configReducer,
  history: playerHistoryReducer,
  table: tableReducer,
  media: mediaSlice.reducer,
  video: videoReducer,
  notifications: notificationReducer,
  dialog: dialogReducer,
  round: roundReducer,
  game: gameReducer,
  user: userReducer,
  locale: localeReducer,
  error: errorReducer,
  tips: tipsReducer,
  lobby: lobbyReducer,
  menu: menuReducer,
  ui: uiReducer,
  isAuthenticated: authReducer,
  performance: performanceReducer,
  externalGameTypes: externalGameTypesReducer,
  app: appReducer,
  preloader: preloaderReducer,
  socket: socketReducer,
  flyingChips: flyingChipsReducer,
  bonus: bonusReducer,
};

const seedEpics = [
  configEpic,
  localeEpic,
  analyticsEpic,
  menuEpic,
  mediaEpic,
  gameEpic,
  soundEpic,
  errorEpic,
  videoEpic,
  wakeLockEpic,
  playerHistoryEpic,
  pingEpic,
  settingsEpic,
  tipsEpic,
  lobbyEpic,
  roundEpic,
  realityCheckEpic,
  incidentsEpic,
  postMessageEpic,
  regulationsEpic,
  regulationsEpicOSS,
  bonusEpic,
];

export {
  constants,
  actions,
  seedEpics as epics,
  bootstrapReducers as reducers,
  selectors,
};

type ReducerMap = typeof bootstrapReducers;
export type GameReducerMap = Partial<ReducerMap> & Record<string, Reducer>;

export type HandleOperatorErrorCallback = (
  socketMessage: GenericPayloadType,
  state: RootState
) => Observable<any>;

export type HandleErrorCallback = (error: GenericPayloadType) => Observable<any>;

type OverridableEpicsMap = {
  realtimeBalance: Epic;
}

export type StoreOverrideProps = {
  epics?: Epic[];
  overridableEpics?: Partial<OverridableEpicsMap>;
  reducers?: GameReducerMap;
  preloadedState?: Record<string, any>;
  handleOperatorError?: HandleOperatorErrorCallback;
  handleError?: HandleErrorCallback;
};

export interface LazyLoadedSlices {}

const isDev = import.meta.env.MODE === 'development';

const setupStore = (
  reducers: StoreOverrideProps['reducers'] = {},
  preloadedState: StoreOverrideProps['preloadedState'] = {},
  middlewares: any[] = []
) => {
  const epicMiddleware = createEpicMiddleware();

  const baseReducer = combineSlices({
    ...bootstrapReducers,
    ...reducers,
  }).withLazyLoadedSlices<LazyLoadedSlices>();

  const enhancedReducer = isDev ? createSnapshotEnhancer(baseReducer) : baseReducer;

  const store = configureStore({
    reducer: enhancedReducer,
    preloadedState: preloadedState as any,
    middleware: (getDefaultMiddleware) => {
      const defaultMiddleware = getDefaultMiddleware({ serializableCheck: false });
      defaultMiddleware.push(...middlewares);
      defaultMiddleware.push(epicMiddleware);
      return defaultMiddleware;
    },
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers(),
    devTools: {
      actionSanitizer: (action: any) => {
        if (
          action.type === actions.lobbyActions.lobby.patch.type
          && action.payload.authTables
        ) {
          return { ...action, payload: '<<BIG_JSON>>' };
        }

        if (
          action.type === actions.localeActions.locale.set.type
          && action.payload.locales
        ) {
          return { ...action, locales: '<<BIG_JSON>>' };
        }

        return action;
      },
      stateSanitizer: (state: any) => {
        const { lobby, locale, ...rest } = state;
        return {
          ...(lobby && { lobby: { ...lobby, authTables: '<<BIG_JSON>>' } }),
          ...(locale && { locale: { ...locale, locales: '<<BIG_JSON>>' } }),
          ...rest,
        };
      },
    },
  });

  return { store, baseReducer, epicMiddleware };
};

const overridableEpicsDefault: OverridableEpicsMap = {
  realtimeBalance: realtimeBalanceEpic,
};

export const createStore = ({
  epics = [],
  overridableEpics = {},
  reducers = {},
  preloadedState = {},
  handleOperatorError,
  handleError,
}: StoreOverrideProps) => {
  let controlPanelTools;
  if (isDev) {
    controlPanelTools = createControlPanelTools();
    (window as any).cp = controlPanelTools;
  }

  const epicOverrides = Object.values(mergeDeepRight(overridableEpicsDefault, overridableEpics));

  const epic$ = new BehaviorSubject(
    combineEpics<any, any, any>(
      ...(controlPanelTools ? [ controlPanelTools.epic ] : []),
      socketEpic({ handleError, handleOperatorError }),
      ...seedEpics,
      ...epics,
      ...epicOverrides
    )
  );

  const rootEpic: Epic = (...epicArgs) => epic$.pipe(mergeMap((epic) => epic(...epicArgs)));

  const { store, baseReducer, epicMiddleware } = setupStore(
    reducers,
    preloadedState,
    [ ...(controlPanelTools ? [ controlPanelTools.middleware ] : []) ]
  );

  const addEpic = (epic: Epic): any => epic$.next(epic);

  const addReducer = async (reducer: Slice) => {
    baseReducer.inject(reducer);
  };

  if (import.meta.env.PROD !== true) {
    // @ts-ignore
    window.__REDUX_STORE__ = store;
  }

  epicMiddleware.run(rootEpic);

  return { store, rootEpic, addEpic, addReducer, baseReducer };
};

export type StoreType = ReturnType<typeof setupStore>['store'];
